import sessionHandling from "services/utils/notificationUtils";
import { customGetAPI } from "services/utils/CustomAPI";
import {
  canUseCompanyContext,
  provisioningCacheKey,
  provisioningModule,
  scopedProvisioning,
} from "services/utils/companyAccess";

// Global Search APIs (Global Search — Frontend API Guide, 04 Nov 2026).
// All results are filtered on the server by the user's permissions, so
// whatever comes back is shown as is.
//   GET  search/?q=&limit=&company_id=   grouped results
//   GET  search/recent/?company_id=      up to 6 recently opened
//   POST search/recent/ { id }           a registry page was opened
//   GET  search/registry/?company_id=    everything the user may open

export const GROUP_LIMIT = 5;
export const SEE_ALL_LIMIT = 20;
export const GROUP_KEYS = ["pages", "settings", "actions", "employees"];

const request = async ({ domain, path, method = "GET", query, body, signal }) => {
  const search = new URLSearchParams(
    Object.entries(query || {}).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  ).toString();
  const response = await fetch(`${domain.replace(/\/$/, "")}/search/${path}${search ? `?${search}` : ""}`, {
    method,
    signal,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Token ${localStorage.getItem("token")}`,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new Error("SESSION_EXPIRED");
  }
  if (response.status === 409) sessionHandling();
  if (response.status === 204) return null;
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.detail || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return data;
};

// Grouped results, in the API's order. `more` says which groups have more
// than `limit` (shows "See all results").
export const search = async ({ domain, companyId, query, limit = GROUP_LIMIT, signal }) => {
  const data = await request({ domain, path: "", query: { q: query, limit, company_id: companyId }, signal });
  const groups = {};
  GROUP_KEYS.forEach((key) => {
    groups[key] = Array.isArray(data?.[key]) ? data[key] : [];
  });
  return {
    ...groups,
    queryTooShort: Boolean(data?.query_too_short),
    more: Object.fromEntries(GROUP_KEYS.map((key) => [key, Boolean(data?.has_more?.[key])])),
  };
};

export const getRecent = async ({ domain, companyId, signal }) => {
  const data = await request({ domain, path: "recent/", query: { company_id: companyId }, signal });
  return Array.isArray(data?.recent) ? data.recent : [];
};

// Fire and forget: Recent is not worth an error message.
export const addRecent = ({ domain, id }) => {
  if (!domain || !id) return;
  request({ domain, path: "recent/", method: "POST", body: { id } }).catch(() => {});
};

export const getRegistry = async ({ domain, companyId, signal }) => {
  const data = await request({ domain, path: "registry/", query: { company_id: companyId }, signal });
  return Array.isArray(data?.items) ? data.items : [];
};

// ---- Permissions for the second check (searchAccess.js) ----
// One provisioning payload per product (the same request the sidebar
// makes), cached for 5 minutes. Resolves null when it can't be loaded, so
// that product's results stay hidden.
const ACCESS_TTL_MS = 5 * 60 * 1000;
const accessCache = new Map();

export const loadProductAccess = ({ domain, userId, userType, companyId, companies, productName }) => {
  if (!canUseCompanyContext(companyId, userType, companies)) {
    return Promise.resolve({ product_name: productName, company_id: companyId, module_details: [] });
  }
  const key = provisioningCacheKey(domain, userId, companyId, productName);
  const cached = accessCache.get(key);
  if (cached && Date.now() - cached.at < ACCESS_TTL_MS) return cached.promise;
  const promise = customGetAPI(
    `${domain}product/access/provisioning/?company_id=${companyId}&module=${encodeURIComponent(provisioningModule(companyId, productName))}`,
  )
    .then((response) => (response?.ok ? scopedProvisioning(response.responseDetails, companyId, productName) : null))
    .catch(() => null)
    .then((access) => {
      if (!access) accessCache.delete(key);
      return access;
    });
  accessCache.set(key, { at: Date.now(), promise });
  return promise;
};

// Permissions can change at any time (ACCESS_CHANGED_EVENT): start over.
export const clearProductAccessCache = () => accessCache.clear();

// ---- Recent kept in this browser too ----
// Every page opened, newest first, per user and company. It covers pages
// the server's registry doesn't list yet (it rejects those ids), and keeps
// the page just opened on top even before the server has stored it.
const LOCAL_RECENT_KEPT = 20;
const localRecentKey = ({ domain, userId, companyId }) => `globalSearchRecent:${domain}:${userId}:${companyId}`;

export const getLocalRecent = (scope) => {
  if (!scope?.userId) return [];
  try {
    const list = JSON.parse(localStorage.getItem(localRecentKey(scope)) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

// `key` identifies the page (its route), so a page is listed once.
export const addLocalRecent = (scope, key, entry) => {
  if (!scope?.userId || !key || !entry) return;
  const item = { key, id: entry.id, type: entry.type, title: entry.title, path: entry.path, route: entry.route };
  const next = [item, ...getLocalRecent(scope).filter((old) => old.key !== key)].slice(0, LOCAL_RECENT_KEPT);
  try {
    localStorage.setItem(localRecentKey(scope), JSON.stringify(next));
  } catch {
    // Storage blocked: only the server's Recent is shown.
  }
};
